import { useQuery } from '@tanstack/react-query'
import {
  getShippingConfiguration,
  updateShippingConfiguration
} from '@/api/settings-api'
import { Country } from '@/domain/countries'
import { useEcommerceId } from '@/hooks/use-ecommerce-id'
import { usePortalTranslate } from '@/hooks/use-portal-translate'
import { ShippingConfiguration } from '@/hooks/use-available-shipping-rules'
import { useSettingsMutation } from '@/application/manage-settings'
import {
  ShippingRule,
  toBackendShippingConfig,
  toFrontendShippingConfig
} from './shipping-config'

const SHIPPING_CONFIG_KEY = ['shippingConfig']

const shippingConfigKey = (ecommerceId?: string) => [
  ...SHIPPING_CONFIG_KEY,
  ecommerceId
]

// Undefined rules mean they did not load, never that the store has none.
export function useShippingConfig(countries: Country[]) {
  const { trans } = usePortalTranslate({})
  const ecommerceId = useEcommerceId()
  // Conditions name countries the way the list does, so the rules wait for it.
  const enabled = !!ecommerceId && countries.length > 0

  const query = useQuery({
    queryKey: shippingConfigKey(ecommerceId),
    queryFn: async () =>
      toFrontendShippingConfig(
        await getShippingConfiguration(ecommerceId ?? ''),
        countries,
        trans
      ),
    enabled
  })

  return {
    originalShippingRules: query.data,
    loadingShippingConfig: query.isLoading,
    reloadShippingConfig: () => {
      if (enabled) query.refetch()
    }
  }
}

export function useSaveShippingConfig(
  availableConfiguration: ShippingConfiguration,
  countries: Country[],
  reasonKeys: string[]
) {
  const { trans } = usePortalTranslate({})
  const ecommerceId = useEcommerceId()

  const { save, saving } = useSettingsMutation({
    mutationFn: (rules: ShippingRule[]) =>
      updateShippingConfiguration(
        ecommerceId ?? '',
        toBackendShippingConfig(
          rules,
          availableConfiguration,
          countries,
          trans,
          reasonKeys
        )
      ),
    invalidate: shippingConfigKey(ecommerceId),
    successKey: 'shipping_costs.save_success',
    errorKey: 'shipping_costs.save_error'
  })

  // settings-api replaces every stored rule with these: hence the loaded-rules guard in save.
  return { saveShippingConfig: save, loadingNewShippingConfig: saving }
}
